/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT
 * Date:         2026-02-10
 * Modified By:  Claude Code
 *
 * Description:
 * Integrated signature saving into the "Save and next" button. When
 * signature is dirty, Save and next captures the signature image and
 * saves it alongside the form data. Removed separate Save Signature
 * button. Save and next is now enabled when EITHER form OR signature
 * is dirty.
 *
 * File Size Check:
 * - Current: 135
 * - Adding: ~15
 * - Projected: ~150
 * - Decision: Proceed
 *
 * Related Files:
 * - client/src/components/setup/user-profile/components/BroadcastSignature.tsx
 * - client/src/components/setup/user-profile/hooks/useUserForm.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    REMOVE_PROFILE_TITLE
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Removed "Create your personal profile" title from the user profile form.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/UserProfileForm.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    REFACTORED_USER_PROFILE_FORM
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Refactored UserProfileForm into smaller components for maintainability 
 * and to comply with line limits. Implemented fix for optional fields 
 * deletion bug using form helpers.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/user-profile/UserProfileForm.tsx
 */
import React, { useRef } from "react";
import { Form, Row, Col, Card, Modal, Button } from "react-bootstrap";
import { useUserForm } from "./hooks/useUserForm";
import { PersonalInformation } from "./components/PersonalInformation";
import { AboutMe } from "./components/AboutMe";
import { SocialMedia } from "./components/SocialMedia";
import { BroadcastSignature, type BroadcastSignatureHandle } from "./components/BroadcastSignature";
import SetupBottomNavigation from "../SetupBottomNavigation";
import { useNavigate } from "react-router-dom";
import { hasErrors, touchedFromErrors, scrollToFirstError } from "../../../utils/formikErrorHelpers";

const UserProfileForm: React.FC = () => {
  const navigate = useNavigate();
  // [2026-02-10] MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT: ref to get signature payload on save
  const signatureRef = useRef<BroadcastSignatureHandle>(null);
  const {
    formik,
    profile,
    loading,
    broadcastSignatureHtml,
    setBroadcastSignatureHtml,
    isSignatureDirty,
    setIsSignatureDirty,
    handleSaveSignature,
    dbPresence,
    nextSectionRef,
    // [2026-02-13] CURRENCY_PHASE2: Currency change confirmation
    showCurrencyConfirm,
    confirmCurrencyChange,
    cancelCurrencyChange,
    pendingInventoryCount,
    originalCurrencyRef,
  } = useUserForm();

  if (loading && !profile) {
    return <div>Loading profile...</div>;
  }

  return (
    <Form onSubmit={formik.handleSubmit}>
      {/* [2025-12-29] REMOVE_PROFILE_TITLE: Removed "Create your personal profile" title */}
      {/* [2026-02-13] Maya: Col lg so columns stack on tablet, split only on desktop */}
      <Row>
        <Col lg={6}>
          <Card className="mb-3 border-0">
            <Card.Body>
              <PersonalInformation formik={formik} dbPresence={dbPresence} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="mb-3 border-0">
            <Card.Body>
              <AboutMe formik={formik} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          <Card className="mb-3 border-0">
            <Card.Body>
              <Card.Title className="company-profile-title">Social media (optional)</Card.Title>
              <SocialMedia formik={formik} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="mb-3 border-0">
            <Card.Body>
              <Card.Title className="company-profile-title">My Broadcast Signature</Card.Title>
              <BroadcastSignature
                ref={signatureRef}
                broadcastSignatureHtml={broadcastSignatureHtml}
                setBroadcastSignatureHtml={setBroadcastSignatureHtml}
                setIsSignatureDirty={setIsSignatureDirty}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <SetupBottomNavigation
        currentSection="profile"
        onBack={() => navigate("/setup?section=company")}
        onSkip={() => navigate("/setup/reviewuser")}
        onSaveAndNext={async () => {
          if (formik.isSubmitting) return;

          // Validate on click; if invalid, touch fields to show errors (e.g., required Country)
          const errors = await formik.validateForm();
          if (hasErrors(errors)) {
            formik.setTouched(touchedFromErrors(errors), true);
            scrollToFirstError(errors);
            return;
          }

          // [2026-02-10] MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT: save signature alongside form
          if (isSignatureDirty && signatureRef.current) {
            await handleSaveSignature(async () => {
              const payload = await signatureRef.current!.getSignaturePayload();
              return payload;
            });
            setIsSignatureDirty(false);
          }

          nextSectionRef.current = "reviewuser";
          await formik.submitForm();
        }}
        isSubmitting={formik.isSubmitting}
        skipDisabled={formik.isSubmitting}
        // [2026-02-10] MERGE_SAVE_SIGNATURE_INTO_SAVE_NEXT: enable when form OR signature is dirty
        saveNextDisabled={(!formik.dirty && !isSignatureDirty) || formik.isSubmitting}
      />

      {/* [2026-02-13] CURRENCY_PHASE2: Currency change confirmation modal */}
      <Modal show={showCurrencyConfirm} onHide={cancelCurrencyChange} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Currency?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Changing your currency from <strong>{originalCurrencyRef.current}</strong> to{" "}
          <strong>{formik.values.currency}</strong> will delete all your current inventory
          (<strong>{pendingInventoryCount} items</strong>).
          You will need to re-upload your inventory in the new currency.
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-figma-1" onClick={cancelCurrencyChange}>
            Cancel
          </Button>
          <Button className="btn-warning" onClick={confirmCurrencyChange}>
            Delete &amp; Change Currency
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
};

export default UserProfileForm;
